import { FileText } from 'lucide-react'
import * as React from 'react'

import { cn } from '../../utils/cn'
import { Button } from './button'
import { Dropzone, type DropzoneProps } from './dropzone'

interface MultipleDropzoneProps extends Omit<DropzoneProps, 'onChange' | 'onMultipleChange' | 'multiple'> {
  /** Callback when files change */
  onChange?: (files: File[]) => void
  maxWidth?: number
  /** Already-uploaded files, shown in the same list as picked files (clickable, not removable). */
  uploadedFiles?: { name: string; url?: string | null }[]
  /** Reaches the inner Dropzone — `className` styles this wrapper, not the drop target. */
  dropzoneClassName?: string
}

/** One row in the file list: the file name (a link when `url` is set) + an optional Remove button. */
function FileRow({
  name,
  nameMaxWidth,
  url,
  onRemove,
}: {
  name: string
  nameMaxWidth: number
  url?: string | null
  onRemove?: () => void
}) {
  return (
    <li className="mt-0 flex items-center justify-between pt-0">
      <div className="flex items-center gap-2">
        <span className="truncate text-sm font-medium">
          <FileText className="size-4" />
        </span>
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            style={{ maxWidth: nameMaxWidth }}
            className="text-ink truncate text-sm font-medium hover:underline"
          >
            {name}
          </a>
        ) : (
          <span style={{ maxWidth: nameMaxWidth }} className="truncate text-sm font-medium">
            {name}
          </span>
        )}
      </div>
      {onRemove && (
        <Button
          type="button"
          onClick={onRemove}
          variant="link"
          aria-label={`Remove ${name}`}
          className="hover:text-destructive h-4"
        >
          Remove
        </Button>
      )}
    </li>
  )
}

/**
 * MultipleDropzone component that allows selecting multiple files.
 * Reuses the existing Dropzone component and lists all selected files below.
 *
 * @example
 * ```tsx
 * const [files, setFiles] = useState<File[]>([])
 *
 * <MultipleDropzone
 *   onChange={setFiles}
 *   accept=".csv,.pdf"
 *   maxSizeMB={10}
 * />
 * ```
 */
function MultipleDropzone({
  onChange,
  className,
  dropzoneClassName,
  maxWidth = 488,
  uploadedFiles = [],
  ...props
}: MultipleDropzoneProps) {
  const [files, setFiles] = React.useState<File[]>([])

  const handleFiles = (newFiles: File[]) => {
    setFiles((prev) => {
      const unique = newFiles.filter(
        (f) => !prev.some((existing) => existing.name === f.name && existing.size === f.size)
      )
      if (unique.length === 0) return prev
      const next = [...prev, ...unique]
      onChange?.(next)
      return next
    })
  }

  const handleRemove = (index: number) => {
    setFiles((prev) => {
      const next = prev.filter((_, i) => i !== index)
      onChange?.(next)
      return next
    })
  }

  return (
    <div style={{ maxWidth }} className={cn('flex w-full flex-col gap-3', className)}>
      <Dropzone
        {...props}
        multiple
        onMultipleChange={handleFiles}
        maxWidth={maxWidth}
        className={dropzoneClassName}
      />
      {(uploadedFiles.length > 0 || files.length > 0) && (
        <ul className="mt-3 flex flex-col gap-3">
          {uploadedFiles.map((file, index) => (
            <FileRow
              key={`uploaded-${file.name}-${index}`}
              name={file.name}
              url={file.url}
              nameMaxWidth={maxWidth - 180}
            />
          ))}
          {files.map((file, index) => (
            <FileRow
              key={`${file.name}-${file.size}-${index}`}
              name={file.name}
              nameMaxWidth={maxWidth - 180}
              onRemove={() => handleRemove(index)}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

export { MultipleDropzone }
export type { MultipleDropzoneProps }
